/**
 * MilestoneCelebration — full-screen 2-second hero card + confetti burst
 * fired when something worth celebrating happens (store hits goal, contest
 * ticket won, personal capture milestone). Auto-dismisses; user can also
 * tap anywhere to close early.
 *
 * Trigger pattern: parent component sets a `milestone` object like
 * { kind: "goal_hit", title: "🎯 GOAL HIT!", subtitle: "Lakewood @ 38%" }
 * and clears it after the animation completes.
 */
import { useEffect } from "react";
import confetti from "canvas-confetti";
import { playVictory } from "../lib/sounds";

const FB_COLORS = ["#0066FF", "#FACC15", "#EC4899", "#16A34A", "#FFFFFF"];

export default function MilestoneCelebration({ milestone, onDone }) {
  useEffect(() => {
    if (!milestone) return undefined;
    // Fire victory chime + confetti the moment this mounts. Independent of
    // the visual card so even if the user dismisses early the celebration
    // still feels complete.
    playVictory();
    const end = Date.now() + 1500;
    const frame = () => {
      confetti({ particleCount: 6, angle: 60, spread: 65, origin: { x: 0, y: 0.85 }, colors: FB_COLORS });
      confetti({ particleCount: 6, angle: 120, spread: 65, origin: { x: 1, y: 0.85 }, colors: FB_COLORS });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
    // Big center burst at start for the "wow" moment
    confetti({ particleCount: 90, spread: 110, origin: { x: 0.5, y: 0.45 }, colors: FB_COLORS, startVelocity: 45 });
    const t = setTimeout(() => onDone && onDone(), 2400);
    return () => clearTimeout(t);
  }, [milestone, onDone]);

  if (!milestone) return null;

  return (
    <div
      onClick={() => onDone && onDone()}
      className="fixed inset-0 z-[80] flex items-center justify-center cursor-pointer milestone-fade-in"
      style={{ background: "radial-gradient(circle at center, rgba(0, 102, 255, 0.35) 0%, rgba(0,0,0,0.55) 70%)", backdropFilter: "blur(4px)" }}
      data-testid="milestone-celebration"
    >
      <div
        className="text-center px-6 milestone-hero-pop"
        style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
      >
        <p
          className="text-6xl sm:text-8xl font-black text-white tracking-tight leading-none"
          style={{ textShadow: "0 6px 30px rgba(0, 102, 255, 0.7)" }}
          data-testid="milestone-title"
        >
          {milestone.title}
        </p>
        {milestone.subtitle && (
          <p className="mt-3 text-xl sm:text-2xl font-black text-yellow-300 uppercase tracking-widest" data-testid="milestone-subtitle">
            {milestone.subtitle}
          </p>
        )}
        <p className="mt-6 text-xs text-white/70 uppercase tracking-widest font-bold">Tap to dismiss</p>
      </div>
    </div>
  );
}
